/**
 * Referrals on the web (docs/REFERRALS.md). The words and rules come from core referrals.ts through the API; this
 * file keeps the link's code until setup finishes, and builds the sms: / mailto: links. We never send texts or read
 * contacts: the person's own texting or email app opens with the words filled in.
 */
import { REFERRAL_LEDE, REFERRAL_TITLE, monthsWord, normalizeReferralCode, normalizeReferralInviteId } from "@logwave/core";
import { smsHref } from "./invites.ts";

export { smsHref };
export const REF_KEY = "cc.ref";
export interface StoredRef { code: string; inviteId?: string }

/** A visit from cleancleaver.com/r/<code>/<invite> lands on /?ref=<code>&invite=<id>: keep it until setup is done. */
export function captureRef(search: string, storage: Storage | undefined = safeStorage()): StoredRef | null {
  const q = new URLSearchParams(search);
  const code = normalizeReferralCode(q.get("ref") ?? "");
  if (!code) return null;
  const inviteId = normalizeReferralInviteId(q.get("invite") ?? "") ?? undefined;
  const ref: StoredRef = { code, ...(inviteId ? { inviteId } : {}) };
  try { storage?.setItem(REF_KEY, JSON.stringify(ref)); } catch { /* private mode: setup just won't know */ }
  return ref;
}
export function storedRef(storage: Storage | undefined = safeStorage()): StoredRef | null {
  try {
    const v = JSON.parse(storage?.getItem(REF_KEY) ?? "null") as StoredRef | null;
    return v && normalizeReferralCode(v.code) ? v : null;
  } catch { return null; }
}
export function clearRef(storage: Storage | undefined = safeStorage()): void {
  try { storage?.removeItem(REF_KEY); } catch { /* ignore */ }
}
function safeStorage(): Storage | undefined {
  try { return typeof localStorage === "undefined" ? undefined : localStorage; } catch { return undefined; }
}

export function mailtoHref(subject: string, body: string): string {
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

/** "2 free months" / "1 free month". */
export const monthsText = monthsWord;
export { REFERRAL_LEDE, REFERRAL_TITLE };

/** Pill tone for each state. */
export const STATE_TONE = { sent: "neutral", opened: "info", pending: "warn", counted: "go", revoked: "stop", not_counted: "stop" } as const;

/** Reduced motion: the celebration card shows without the whoosh. */
export function prefersReducedMotion(): boolean {
  try { return typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches; } catch { return false; }
}
